import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * The one look for "Elliott is making something new": building a program,
 * the Program Plan, drafting levels, writing a preview. The pulsing sparkle
 * means Elliott is working and nothing else, so it is never used for a plain
 * page load (that is a dark skeleton) or inside a button (that is a label
 * change, "Saving...").
 *
 * - `size="full"` fills its area, for long builds.
 * - `size="inline"` sits in place of one section while it is written.
 * - `progress` shows a count and a thin bar when the app knows one
 *   ("3 of 12 lessons").
 */
const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.88); }
  50% { opacity: 1; transform: scale(1.06); }
`;

export interface ElliottWorkingProgress {
  done: number;
  total: number;
  /** What is being counted, plural: "lessons". */
  unit: string;
}

const ElliottWorking: React.FC<{
  /** One line saying what Elliott is doing: "Elliott is writing your lessons..." */
  message: string;
  size?: 'full' | 'inline';
  progress?: ElliottWorkingProgress | null;
}> = ({ message, size = 'inline', progress }) => {
  const full = size === 'full';
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={message}
      sx={{
        display: 'flex',
        flexDirection: full ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: full ? 'center' : 'flex-start',
        gap: full ? '16px' : '12px',
        py: full ? 8 : '14px',
        px: full ? 2 : '16px',
        width: '100%',
        flex: full ? 1 : undefined,
        textAlign: full ? 'center' : 'left'
      }}
    >
      <AutoAwesomeIcon
        sx={{
          flexShrink: 0,
          fontSize: full ? 44 : 20,
          color: '#F79420',
          animation: `${pulse} 1.6s ease-in-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: full ? 'center' : 'flex-start', gap: '8px' }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: full ? 16 : 13.5,
            fontWeight: 500,
            lineHeight: 1.4,
            color: '#B6B6B6'
          }}
        >
          {message}
        </Typography>
        {progress && pct !== null && (
          <>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#868686' }}>
              {progress.done} of {progress.total} {progress.unit}
            </Typography>
            <Box sx={{ width: 200, height: 4, bgcolor: '#2A2A2A', borderRadius: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${pct}%`,
                  height: '100%',
                  bgcolor: '#F79420',
                  borderRadius: 2,
                  transition: 'width 0.4s ease'
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ElliottWorking;
